import type { BudgetUpdateReportLine } from "~/ui-rtk/api/budgetUpdateReportsApi"
import { addDays } from "../hooks/useBudgetRecommendations"

/** One day of an entity's performance from the paid-media cubes (dollars). */
export interface DailyPerformance {
  /** YYYY-MM-DD */
  date: string
  spend: number
  modeledRevenue: number
  brandValue: number
}

export type PerformanceMetric =
  | "Spend"
  | "Modeled revenue"
  | "DR ROAS"
  | "Brand ROAS"

export const PERFORMANCE_METRICS: PerformanceMetric[] = [
  "Spend",
  "Modeled revenue",
  "DR ROAS",
  "Brand ROAS",
]

const isRatio = (metric: PerformanceMetric) =>
  metric === "DR ROAS" || metric === "Brand ROAS"

/** The metric's value for one day; ROAS is null on a zero-spend day. */
export function dailyMetric(
  day: DailyPerformance,
  metric: PerformanceMetric,
): number | null {
  switch (metric) {
    case "Spend":
      return day.spend
    case "Modeled revenue":
      return day.modeledRevenue
    case "DR ROAS":
      return day.spend > 0 ? day.modeledRevenue / day.spend : null
    case "Brand ROAS":
      return day.spend > 0 ? day.brandValue / day.spend : null
  }
}

/**
 * The metric over a window: ROAS as total revenue / total spend (never an
 * average of daily ratios), money as the daily average. `days` counts the
 * days actually covered so a thin window can be flagged.
 */
export function windowMetric(
  days: DailyPerformance[],
  metric: PerformanceMetric,
): { value: number | null; days: number } {
  if (days.length === 0) return { value: null, days: 0 }
  const spend = days.reduce((sum, d) => sum + d.spend, 0)
  if (metric === "DR ROAS") {
    const revenue = days.reduce((sum, d) => sum + d.modeledRevenue, 0)
    return { value: spend > 0 ? revenue / spend : null, days: days.length }
  }
  if (metric === "Brand ROAS") {
    const brand = days.reduce((sum, d) => sum + d.brandValue, 0)
    return { value: spend > 0 ? brand / spend : null, days: days.length }
  }
  const total =
    metric === "Spend"
      ? spend
      : days.reduce((sum, d) => sum + d.modeledRevenue, 0)
  return { value: total / days.length, days: days.length }
}

export function formatPerformance(
  metric: PerformanceMetric,
  value: number | null,
  compact = false,
): string {
  if (value === null) return "—"
  if (isRatio(metric)) return `${value.toFixed(2)}x`
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: compact ? 0 : 2,
    maximumFractionDigits: compact ? 0 : 2,
  })}`
}

const round1 = (n: number) => Math.round(n * 10) / 10
const round2 = (n: number) => Math.round(n * 100) / 100

const percentChange = (
  before: number | null,
  after: number | null,
): number | null =>
  before !== null && after !== null && before !== 0
    ? round1(((after - before) / before) * 100)
    : null

/**
 * Signed "up/down Δ (±N%)" — every comparison carries BOTH the absolute and
 * the percent move, never one alone (doctrine, Preston 2026-09-15). "flat"
 * when nothing moved, "—" when a side is unknown.
 */
export function formatDeltaPair(
  metric: PerformanceMetric,
  deltaValue: number | null,
  deltaPercent: number | null,
): string {
  if (deltaValue === null) return "—"
  if (deltaValue === 0) return "flat"
  const word = deltaValue > 0 ? "up" : "down"
  const amount = formatPerformance(metric, Math.abs(deltaValue))
  const pct =
    deltaPercent === null
      ? ""
      : ` (${deltaPercent > 0 ? "+" : ""}${deltaPercent}%)`
  return `${word} ${amount}${pct}`
}

/** The doctrine's comparison line: "$A vs $B, up/down $Δ (±N%)". */
export function formatComparison(
  metric: PerformanceMetric,
  before: number | null,
  after: number | null,
): string {
  if (before === null || after === null) return "—"
  return `${formatPerformance(metric, before)} vs ${formatPerformance(metric, after)}, ${formatDeltaPair(
    metric,
    round2(after - before),
    percentChange(before, after),
  )}`
}

/** A recorded change of the selected entity, as the performance view needs it. */
export interface EntityChange {
  id: string
  /** YYYY-MM-DD of the change (local date of the audit row). */
  date: string
  changedAt: string
  metric: string
  label: string
  changedBy: string
  source: string
  /** Doctrine size of the move (≥ 20% or ≥ $250/day = big); null when unreadable. */
  magnitude: "big" | "small" | null
}

export interface BeforeAfter {
  change: EntityChange
  before: { value: number | null; days: number }
  after: { value: number | null; days: number }
  /** after − before in the metric's unit; null until both windows have data. */
  deltaValue: number | null
  deltaPercent: number | null
  /** The after-window is cut short by the data's latest complete day. */
  afterTruncated: boolean
}

interface WindowsAround {
  beforeDays: DailyPerformance[]
  afterDays: DailyPerformance[]
  /** Calendar bounds of the after-window as actually read (capped). */
  afterRange: { from: string; to: string }
  afterTruncated: boolean
  pick: (from: string, to: string) => DailyPerformance[]
}

/**
 * The `windowDays` either side of a change. The change day itself is
 * excluded from both — it ran partly on each budget. The after-window never
 * extends past the latest complete day of data, so a change made yesterday
 * reads "no data yet" rather than a partial-day number.
 */
function windowsAround(
  days: DailyPerformance[],
  changeDate: string,
  windowDays: number,
  latestCompleteDate: string | null,
): WindowsAround {
  const byDate = new Map(days.map(d => [d.date, d]))
  const pick = (from: string, to: string): DailyPerformance[] => {
    const out: DailyPerformance[] = []
    for (let d = from; d <= to; d = addDays(d, 1)) {
      const day = byDate.get(d)
      if (day) out.push(day)
    }
    return out
  }
  const beforeDays = pick(
    addDays(changeDate, -windowDays),
    addDays(changeDate, -1),
  )
  const afterEnd = addDays(changeDate, windowDays)
  const cappedEnd =
    latestCompleteDate && latestCompleteDate < afterEnd
      ? latestCompleteDate
      : afterEnd
  const afterStart = addDays(changeDate, 1)
  const afterDays = cappedEnd >= afterStart ? pick(afterStart, cappedEnd) : []
  return {
    beforeDays,
    afterDays,
    afterRange: { from: afterStart, to: cappedEnd },
    afterTruncated: cappedEnd < afterEnd,
    pick,
  }
}

/**
 * Compare the metric over the `windowDays` before a change with the
 * `windowDays` after it (see `windowsAround` for the window rules).
 */
export function beforeAfter(
  days: DailyPerformance[],
  change: EntityChange,
  metric: PerformanceMetric,
  windowDays: number,
  latestCompleteDate: string | null,
): BeforeAfter {
  const { beforeDays, afterDays, afterTruncated } = windowsAround(
    days,
    change.date,
    windowDays,
    latestCompleteDate,
  )
  const before = windowMetric(beforeDays, metric)
  const after = windowMetric(afterDays, metric)
  return {
    change,
    before,
    after,
    deltaValue:
      before.value !== null && after.value !== null
        ? round2(after.value - before.value)
        : null,
    deltaPercent: percentChange(before.value, after.value),
    afterTruncated,
  }
}

// ---------------------------------------------------------------------------
// Marginal return — "does the marginal dollar pay back"
// ---------------------------------------------------------------------------

/** Which revenue the marginal read divides by: modeled revenue (DR basis) or brand value (brand basis). */
export type RevenueBasis = "modeledRevenue" | "brandValue"

/**
 * The marginal-returns Sheet's verdict vocabulary, mirrored. Read on the
 * entity's own before/after windows: Δ revenue/day ÷ Δ spend/day.
 *  - spend up:   ADD (≥ 1.00x) · HOLD (0–1x: the last dollar paid back under 1.00x) · PULL BACK (revenue fell on added spend)
 *  - spend down: RESTORE (the removed dollars were returning ≥ 1.00x) · CUT HELD (they returned under 1.00x, or revenue rose anyway)
 *  - FLAT: the spend move is under the 5% materiality floor
 *  - INSUFFICIENT DATA: fewer than 3 complete days after the change (fail closed — never estimated)
 */
export type MarginalVerdict =
  | "ADD"
  | "HOLD"
  | "PULL BACK"
  | "RESTORE"
  | "CUT HELD"
  | "FLAT"
  | "INSUFFICIENT DATA"

/** Complete after-days a read needs before it says anything (the 3–5 day monitoring read). */
export const MIN_READ_DAYS = 3
/** Spend moves under this share of the before spend/day are FLAT. */
export const MATERIALITY_PCT = 5
/** Marginal return at or above this is ADD / RESTORE. */
export const MARGINAL_FLOOR = 1

interface MarginalSide {
  spendPerDay: number | null
  revenuePerDay: number | null
  days: number
}

export interface MarginalRead {
  basis: RevenueBasis
  before: MarginalSide
  after: MarginalSide
  /** after − before, dollars per day; null until both windows have data. */
  deltaSpendPerDay: number | null
  deltaRevenuePerDay: number | null
  /** Δ revenue ÷ Δ spend — what the added (or removed) dollars returned. Null when FLAT or insufficient. */
  marginalRoas: number | null
  verdict: MarginalVerdict
  /** What the numbers say — a finding, not a prescription. */
  reason: string
}

const signedMoney = (n: number) =>
  `${n < 0 ? "-" : "+"}${formatPerformance("Spend", Math.abs(n))}`

/**
 * The total-spend gate, read per entity: did the dollars this change added
 * (or removed) pay back at the margin? Revenue here is the ENTITY's modeled
 * revenue or brand value from the paid-media cubes — not the Sheet's
 * brand-level new-customer revenue — so it is the entity's own marginal
 * read, labelled as such.
 */
export function marginalRead(
  days: DailyPerformance[],
  change: EntityChange,
  windowDays: number,
  latestCompleteDate: string | null,
  basis: RevenueBasis,
): MarginalRead {
  const { beforeDays, afterDays } = windowsAround(
    days,
    change.date,
    windowDays,
    latestCompleteDate,
  )
  const side = (rows: DailyPerformance[]): MarginalSide => {
    if (rows.length === 0) {
      return { spendPerDay: null, revenuePerDay: null, days: 0 }
    }
    const spend = rows.reduce((s, d) => s + d.spend, 0)
    const revenue = rows.reduce((s, d) => s + d[basis], 0)
    return {
      spendPerDay: round2(spend / rows.length),
      revenuePerDay: round2(revenue / rows.length),
      days: rows.length,
    }
  }
  const before = side(beforeDays)
  const after = side(afterDays)
  const base = {
    basis,
    before,
    after,
    deltaSpendPerDay: null,
    deltaRevenuePerDay: null,
    marginalRoas: null,
  }
  if (
    before.spendPerDay === null ||
    before.revenuePerDay === null ||
    after.spendPerDay === null ||
    after.revenuePerDay === null ||
    after.days < MIN_READ_DAYS
  ) {
    return {
      ...base,
      verdict: "INSUFFICIENT DATA",
      reason: `${after.days} of ${MIN_READ_DAYS} complete days after the change`,
    }
  }

  const deltaSpendPerDay = round2(after.spendPerDay - before.spendPerDay)
  const deltaRevenuePerDay = round2(after.revenuePerDay - before.revenuePerDay)
  const read = { ...base, deltaSpendPerDay, deltaRevenuePerDay }
  const materiality = (before.spendPerDay * MATERIALITY_PCT) / 100
  if (deltaSpendPerDay === 0 || Math.abs(deltaSpendPerDay) < materiality) {
    return {
      ...read,
      verdict: "FLAT",
      reason: `spend/day ${formatDeltaPair("Spend", deltaSpendPerDay, percentChange(before.spendPerDay, after.spendPerDay))} — under the ${MATERIALITY_PCT}% materiality floor`,
    }
  }

  const marginalRoas = round2(deltaRevenuePerDay / deltaSpendPerDay)
  const reason = `Δ spend ${signedMoney(deltaSpendPerDay)}/day, Δ revenue ${signedMoney(deltaRevenuePerDay)}/day → ${marginalRoas.toFixed(2)}x marginal`
  let verdict: MarginalVerdict
  if (deltaSpendPerDay < 0) {
    verdict = marginalRoas >= MARGINAL_FLOOR ? "RESTORE" : "CUT HELD"
  } else if (marginalRoas >= MARGINAL_FLOOR) {
    verdict = "ADD"
  } else if (marginalRoas >= 0) {
    verdict = "HOLD"
  } else {
    verdict = "PULL BACK"
  }
  return { ...read, marginalRoas, verdict, reason }
}

// ---------------------------------------------------------------------------
// Short-window reads: the same weekdays one and two weeks earlier
// ---------------------------------------------------------------------------

/** The short-window rule applies to reads of at most this many days. */
export const MAX_SHORT_READ_DAYS = 7

export interface WeekdayComparison {
  /** "1 week earlier" | "2 weeks earlier" */
  label: string
  from: string
  to: string
  days: number
  value: number | null
  /** read − earlier, in the metric's unit. */
  deltaValue: number | null
  deltaPercent: number | null
}

export interface SameWeekdayReads {
  read: { from: string; to: string; days: number; value: number | null }
  oneWeekEarlier: WeekdayComparison
  twoWeeksEarlier: WeekdayComparison
}

/**
 * When a read covers only a few days after a change (e.g. Sat–Mon), a period
 * average is not enough: compare the same calendar days one week earlier and
 * two weeks earlier, each with $ and % (Preston 2026-09-15). Null when the
 * window is longer than the short-window rule covers, or when no complete
 * after-day exists yet.
 */
export function sameWeekdayReads(
  days: DailyPerformance[],
  change: EntityChange,
  metric: PerformanceMetric,
  windowDays: number,
  latestCompleteDate: string | null,
): SameWeekdayReads | null {
  if (windowDays > MAX_SHORT_READ_DAYS) return null
  const { afterDays, afterRange, pick } = windowsAround(
    days,
    change.date,
    windowDays,
    latestCompleteDate,
  )
  if (afterDays.length === 0) return null
  const read = windowMetric(afterDays, metric)
  const compare = (weeks: number): WeekdayComparison => {
    const from = addDays(afterRange.from, -7 * weeks)
    const to = addDays(afterRange.to, -7 * weeks)
    const earlier = windowMetric(pick(from, to), metric)
    return {
      label: weeks === 1 ? "1 week earlier" : `${weeks} weeks earlier`,
      from,
      to,
      days: earlier.days,
      value: earlier.value,
      deltaValue:
        read.value !== null && earlier.value !== null
          ? round2(read.value - earlier.value)
          : null,
      deltaPercent: percentChange(earlier.value, read.value),
    }
  }
  return {
    read: { ...afterRange, days: read.days, value: read.value },
    oneWeekEarlier: compare(1),
    twoWeeksEarlier: compare(2),
  }
}

/**
 * "Does the lift hold on all three?" — true when every comparison moved the
 * same way (all up or all down), false when they disagree or one is flat,
 * null when any of them is unreadable.
 */
export function liftHolds(deltas: Array<number | null>): boolean | null {
  if (deltas.length === 0 || deltas.some(d => d === null)) return null
  const signs = deltas.map(d => Math.sign(d as number))
  return signs.every(s => s === 1) || signs.every(s => s === -1)
}

const entityKey = (line: BudgetUpdateReportLine) =>
  `${line.platform}|${line.entity}|${line.entityId}`

/**
 * Applied changes of one entity, oldest first, labelled "previous → new,
 * ±$Δ (±N%)" — the $ and the % together, as every comparison line reads.
 */
export function changesForEntity(
  lines: BudgetUpdateReportLine[],
  key: string,
  toLocalDate: (iso: string) => string,
): EntityChange[] {
  return lines
    .filter(l => entityKey(l) === key && l.success && l.metric !== "Status")
    .sort((a, b) => a.changedAt.localeCompare(b.changedAt))
    .map(l => ({
      id: l.id,
      date: toLocalDate(l.changedAt),
      changedAt: l.changedAt,
      metric: l.metric,
      label: `${l.metric}: ${l.previousLabel || "?"} → ${l.newLabel}${l.deltaLabel ? `, ${l.deltaLabel}` : ""}`,
      changedBy: l.changedBy,
      source: l.source,
      magnitude: l.magnitude,
    }))
}
